// Configurações › Empresa: nome, cidade, UF e segmento (o administrador edita; vendedor só vê).
import { formatarDocumento, tipoDocumento } from '@crm/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Toast, useToast } from '../components/ui';

interface Empresa { nome: string; documento: string | null; cidade: string | null; estado: string | null; segmento: string | null }
const UFS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];

export function EmpresaConfig() {
  const { pode } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['config-empresa'], queryFn: () => api<Empresa>('/config/empresa') });
  const [f, setF] = useState({ nome: '', cidade: '', estado: '', segmento: '' });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const pode_ = pode('CONFIGURACOES_GERENCIAR');

  useEffect(() => {
    if (q.data) setF({ nome: q.data.nome, cidade: q.data.cidade ?? '', estado: q.data.estado ?? '', segmento: q.data.segmento ?? '' });
  }, [q.data]);

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await api('/config/empresa', { method: 'PATCH', body: { nome: f.nome, cidade: f.cidade || null, estado: f.estado || null, segmento: f.segmento || null } });
      await Promise.all([qc.invalidateQueries({ queryKey: ['config-empresa'] }), qc.invalidateQueries({ queryKey: ['primeiros-passos'] })]);
      toast.show('Dados da empresa salvos.');
    } catch (err) {
      setErro(mensagemErro(err));
    } finally {
      setSalvando(false);
    }
  }

  if (q.isLoading) return <Carregando />;
  if (q.error) return <Erro erro={mensagemErro(q.error)} />;
  return (
    <section className="card" style={{ maxWidth: 640 }} aria-labelledby="h-empresa">
      <h2 id="h-empresa">Dados da empresa</h2>
      <form onSubmit={salvar} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Erro erro={erro} />
        <div className="form-grid">
          <label className="field span-2"><span>Nome da empresa *</span>
            <input className="input" required minLength={2} maxLength={150} disabled={!pode_} value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} />
          </label>
          <label className="field"><span>Cidade</span>
            <input className="input" maxLength={100} disabled={!pode_} value={f.cidade} onChange={(e) => setF({ ...f, cidade: e.target.value })} />
          </label>
          <label className="field"><span>Estado</span>
            <select className="select" disabled={!pode_} value={f.estado} onChange={(e) => setF({ ...f, estado: e.target.value })}>
              <option value="">—</option>
              {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
          <label className="field span-2"><span>Segmento</span>
            <input className="input" maxLength={100} disabled={!pode_} placeholder="Ex.: distribuidora de medicamentos" value={f.segmento} onChange={(e) => setF({ ...f, segmento: e.target.value })} />
          </label>
        </div>
        {q.data?.documento && <span className="small muted">{tipoDocumento(q.data.documento)} <span className="num">{formatarDocumento(q.data.documento)}</span> (para alterar, fale com o suporte)</span>}
        {pode_ && <div className="modal-foot"><button className="btn primary" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar'}</button></div>}
      </form>
      <Toast msg={toast.msg} onDone={toast.clear} />
    </section>
  );
}
